import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import * as React from 'react';

import { ActionMenu, IconButton, Spinner } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { space } from '@creditornot/alchemy-react/tokens';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { CountrySelector } from 'common/components/cookbook/CountrySelector/CountrySelector';
import { SubmitStateModal } from 'common/components/cookbook/Modals/SubmitStateModal';
import { NavigationBar } from 'common/components/cookbook/NavigationBar/NavigationBar';
import { Stack } from 'common/components/cookbook/Stack';
import { ViewMeta } from 'common/components/cookbook/ViewMeta';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useToast } from 'common/hooks/useToast';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { downloadBlob } from 'common/utils/downloadUtils';
import { SettingsMenu } from 'features/inventory-replenishment/components/SettingsMenu/SettingsMenu';

import { downloadBulkOperationReport, downloadCurrentOverrides } from '../api';
import { BulkOperation } from '../api-types';
import { overrideManagementRouteApi } from '../routeApi';
import { BulkOperationEntry } from './BulkOperationEntry';
import Overrides from './Overrides';
import { isFinalStatus, replaceOperation, sortByCreatedAtDesc } from './utils';
import { BulkUploadOverridesModal } from '../modals/BulkUploadOverridesModal/BulkUploadOverridesModal';
import { DeleteOverridesModal } from '../modals/BulkUploadOverridesModal/DeleteOverridesModal';
import { OverridesHistoryModal } from '../modals/OverridesHistoryModal';
import { UploadOverrideCSVModal } from '../modals/UploadOverrideCSVModal';
import {
  BULK_OPERATIONS_QUERY_KEY_ROOT,
  OVERRIDE_MANAGEMENT_KEY_ROOT,
  useBulkOperationStateQuery,
  useBulkOperationsQuery,
} from '../query';

const SUCCESS_MODAL_TIMEOUT_MS = 700;

export function OverrideManagementView() {
  const format = useFormatMessage();
  const countryCodes = useSelectedCountryCodes();
  const countryCode = countryCodes[0] ?? null;
  const queryClient = useApiQueryClient();

  const bulkUploadOverridesEnabled = useFeatureEnabled('bulk-upload-overrides', countryCodes);

  const { addErrorToast, addSuccessToast } = useToast();

  const navigate = overrideManagementRouteApi.useNavigate();
  const { uploadOverrideCsvModalOpen, deleteOverridesModalOpen } =
    overrideManagementRouteApi.useSearch();

  const pageTitle = format('inventory_replenishment.override_management');

  const [successModalOpen, setSuccessModalOpen] = React.useState(false);
  const [overrideHistoryModalOpen, setOverrideHistoryModalOpen] = React.useState(false);

  function toggleUploadOverrideCsvModal(open: boolean) {
    void navigate({
      search: (prev) => ({
        ...prev,
        uploadOverrideCsvModalOpen: open ? true : undefined,
      }),
    });
  }

  function toggleDeleteOverridesModal(open: boolean) {
    void navigate({
      search: (prev) => ({
        ...prev,
        deleteOverridesModalOpen: open ? true : undefined,
      }),
    });
  }

  function showSuccessModal() {
    setSuccessModalOpen(true);
    setTimeout(() => {
      setSuccessModalOpen(false);
    }, SUCCESS_MODAL_TIMEOUT_MS);
  }

  const { mutate: mutateDownloadOverrides, isPending: downloadOverridesPending } = useMutation({
    mutationFn: () => downloadCurrentOverrides(countryCodes[0]!),
    onSuccess: (blob) => {
      const date = DateTime.now().toISO().slice(0, 10);
      const fileName = `${date}-${countryCodes[0]}-overrides.csv`;
      downloadBlob(new Blob([blob]), fileName);
    },
    onError: addErrorToast,
  });

  const { mutate: mutateDownloadReport, isPending: downloadReportPending } = useMutation({
    mutationFn: (id: string) => downloadBulkOperationReport(id),
    onSuccess: (blob) => {
      const date = DateTime.now().toISO().slice(0, 10);
      const fileName = `${date}-${countryCodes[0]}-bulk-operation-report.csv`;
      downloadBlob(blob, fileName);
    },
    onError: addErrorToast,
  });

  const {
    data: bulkOperationsData,
    error: bulkOperationsError,
    isLoading: bulkOperationsLoading,
  } = useBulkOperationsQuery(countryCode!, bulkUploadOverridesEnabled && Boolean(countryCode));

  const bulkOperations = React.useMemo(() => bulkOperationsData?.items ?? [], [bulkOperationsData]);

  const newestFirstBulkOperations = React.useMemo(
    () => sortByCreatedAtDesc(bulkOperations),
    [bulkOperations],
  );

  // Find the oldest pending operation from the list of operations sorted as newest first,
  // (prefers processing over queued) for polling.
  const oldestPendingOperationFromList = React.useMemo(() => {
    return (
      newestFirstBulkOperations.findLast((operation) => operation.status === 'PROCESSING') ??
      newestFirstBulkOperations.findLast((operation) => operation.status === 'QUEUED')
    );
  }, [newestFirstBulkOperations]);

  const handleBulkOperationStatusChange = React.useCallback(
    (operation: BulkOperation) => {
      if (isFinalStatus(operation.status)) {
        void queryClient.invalidateQueries({
          queryKey: [OVERRIDE_MANAGEMENT_KEY_ROOT],
        });
        void queryClient.invalidateQueries({
          queryKey: [BULK_OPERATIONS_QUERY_KEY_ROOT, countryCode],
        });
      }
    },
    [countryCode, queryClient],
  );

  const { data: polledOperation } = useBulkOperationStateQuery(
    oldestPendingOperationFromList?.id ?? '',
    {
      enabled: Boolean(oldestPendingOperationFromList?.id),
      onStatusChange: handleBulkOperationStatusChange,
    },
  );

  // Merge the polled entity into list data used by main view and history.
  const displayNewestFirstBulkOperations = React.useMemo(
    () => replaceOperation(newestFirstBulkOperations, polledOperation),
    [newestFirstBulkOperations, polledOperation],
  );

  const latestFinishedOperation = displayNewestFirstBulkOperations.find((operation) =>
    isFinalStatus(operation.status),
  );

  const mainViewOperation = oldestPendingOperationFromList
    ? (polledOperation ?? oldestPendingOperationFromList)
    : latestFinishedOperation;

  return (
    <ViewMeta title={pageTitle}>
      <NavigationBar
        title={pageTitle}
        titleContent={<CountrySelector />}
        rightContent={<SettingsMenu initialSetting="forecast" />}
        disableBottomContentPadding
      />
      <Stack gap="narrowSmall">
        <Stack
          justifyContent="space-between"
          direction="row"
          alignItems="center"
          style={{ padding: space.regularXxxlarge, paddingBottom: 0 }}
        >
          {bulkUploadOverridesEnabled &&
            (bulkOperationsLoading ? (
              <Spinner size="tiny" />
            ) : (
              mainViewOperation && (
                <BulkOperationEntry
                  operation={mainViewOperation}
                  downloadReportPending={downloadReportPending}
                  onDownloadReport={() => mutateDownloadReport(mainViewOperation.id)}
                />
              )
            ))}
          <Stack direction="row" gap="regularLarge" style={{ marginLeft: 'auto' }}>
            <IconButton
              size="small"
              variant="secondaryNeutral"
              aria-label={format('forecasting.download_overrides')}
              icon={icons.download}
              onClick={() => mutateDownloadOverrides()}
              disabled={downloadOverridesPending}
            />
            {bulkUploadOverridesEnabled && (
              <IconButton
                size="small"
                variant="secondaryNeutral"
                aria-label={format('forecasting.override_history')}
                icon={icons.clockArrowCounterclockwise}
                onClick={() => setOverrideHistoryModalOpen(true)}
              />
            )}

            {bulkUploadOverridesEnabled ? (
              // Opening modals to upload and delete overrides for new behaviour
              <ActionMenu.Root
                size="medium"
                trigger={
                  <IconButton
                    size="small"
                    variant="primary"
                    aria-label={format('forecasting.upload_overrides')}
                    icon={icons.plus}
                  />
                }
              >
                <ActionMenu.Item
                  label={format('forecasting.upload_overrides')}
                  onClick={() => toggleUploadOverrideCsvModal(true)}
                />
                <ActionMenu.Item
                  label={format('forecasting.delete_overrides')}
                  onClick={() => toggleDeleteOverridesModal(true)}
                />
              </ActionMenu.Root>
            ) : (
              <IconButton
                // Old way to only upload overrides
                size="small"
                variant="primary"
                aria-label={format('forecasting.upload_overrides')}
                icon={icons.plus}
                onClick={() => toggleUploadOverrideCsvModal(true)}
              />
            )}
          </Stack>
        </Stack>
        {bulkUploadOverridesEnabled && <ApiErrorNotification error={bulkOperationsError} />}
      </Stack>
      <Overrides />

      <UploadOverrideCSVModal
        // Old modal supported for backwards compatibility
        isOpen={!bulkUploadOverridesEnabled && Boolean(uploadOverrideCsvModalOpen)}
        onDismiss={() => toggleUploadOverrideCsvModal(false)}
        onSuccess={() => {
          toggleUploadOverrideCsvModal(false);
          showSuccessModal();
          addSuccessToast({ description: format('forecasting.overrides_uploaded') });
        }}
      />
      <BulkUploadOverridesModal
        // New modal for bulk upload overrides
        isOpen={bulkUploadOverridesEnabled && Boolean(uploadOverrideCsvModalOpen)}
        onDismiss={() => toggleUploadOverrideCsvModal(false)}
        onSuccess={() => {
          toggleUploadOverrideCsvModal(false);
          showSuccessModal();
          addSuccessToast({ description: format('forecasting.file_uploaded_for_processing') });
        }}
      />
      <DeleteOverridesModal
        // New modal for deleting overrides with bulk upload behaviour
        isOpen={bulkUploadOverridesEnabled && Boolean(deleteOverridesModalOpen)}
        onDismiss={() => toggleDeleteOverridesModal(false)}
      />
      <SubmitStateModal isSuccess={successModalOpen} />
      <OverridesHistoryModal
        open={overrideHistoryModalOpen}
        onDismiss={() => setOverrideHistoryModalOpen(false)}
        overrides={displayNewestFirstBulkOperations}
      />
    </ViewMeta>
  );
}
